/**
 * ExecutionList Component
 *
 * Displays a list of executions with filtering and pagination.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { NativeSelect as Select } from '../ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from '../ui/card';
import { Pagination } from '../common/Pagination';
import {
  useExecutions,
  getExecutionStatusColor,
  getExecutionStatusIcon,
  formatDuration,
} from '../../hooks/useExecution';
import { ExecutionStatus, Execution } from '../../types/execution';

interface ExecutionListProps {
  /** Optional agent ID to filter by */
  agentId?: string;
  /** Whether to show the agent name column */
  showAgentColumn?: boolean;
  /** Callback when an execution is selected */
  onSelectExecution?: (execution: Execution) => void;
  /** Currently selected execution ID */
  selectedExecutionId?: string;
  /** Maximum items per page */
  pageSize?: number;
}

const STATUS_OPTIONS: { value: ExecutionStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'RUNNING', label: 'Running' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'TIMEOUT', label: 'Timeout' },
];

export function ExecutionList({
  agentId,
  showAgentColumn = true,
  onSelectExecution,
  selectedExecutionId,
  pageSize = 10,
}: ExecutionListProps) {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<ExecutionStatus | 'all'>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Build query params
  const queryParams = {
    page,
    limit: pageSize,
    ...(statusFilter !== 'all' && { status: statusFilter }),
    ...(agentId && { agentId }),
    ...(dateFrom && { from_date: dateFrom }),
    ...(dateTo && { to_date: dateTo }),
  };

  const { data, isLoading, error } = useExecutions(queryParams);
  const executions = data?.data || [];
  const totalPages = data?.meta?.pages || 1;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value as ExecutionStatus | 'all');
    setPage(1);
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const hasFilters = statusFilter !== 'all' || dateFrom || dateTo;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="status-filter">Status</Label>
              <Select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="date-from">From Date</Label>
              <Input
                id="date-from"
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="date-to">To Date</Label>
              <Input
                id="date-to"
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div className="flex items-end">
              {hasFilters && (
                <Button variant="outline" onClick={clearFilters} className="w-full">
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 p-4 rounded-md">
          {error instanceof Error ? error.message : 'Failed to load executions'}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-20 bg-slate-100 rounded-lg animate-pulse"
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && executions.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-4xl mb-4">📋</div>
            <CardTitle className="text-lg mb-2">No Executions Found</CardTitle>
            <CardDescription>
              {hasFilters
                ? 'Try adjusting your filters'
                : 'Run an agent to see execution history here'}
            </CardDescription>
          </CardContent>
        </Card>
      )}

      {/* Execution List */}
      {!isLoading && executions.length > 0 && (
        <div className="space-y-2">
          {executions.map((execution) => (
            <ExecutionRow
              key={execution.id}
              execution={execution}
              showAgentColumn={showAgentColumn}
              isSelected={execution.id === selectedExecutionId}
              onClick={() => onSelectExecution?.(execution)}
              formatDate={formatDate}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {!isLoading && totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}

/**
 * Single execution row component
 */
function ExecutionRow({
  execution,
  showAgentColumn,
  isSelected,
  onClick,
  formatDate,
}: {
  execution: Execution;
  showAgentColumn: boolean;
  isSelected: boolean;
  onClick?: () => void;
  formatDate: (date: string | null) => string;
}) {
  const isClickable = !!onClick;

  return (
    <Card
      // A stable hook for the e2e suite. These render as cards, not table rows,
      // so tests were matching on `tr[class*="execution"]` and silently finding
      // nothing — which read as "the page is empty" rather than "the selector is
      // wrong".
      data-testid="execution-row"
      className={`transition-all ${
        isSelected
          ? 'ring-2 ring-primary border-primary'
          : isClickable
          ? 'hover:shadow-md cursor-pointer'
          : ''
      }`}
      onClick={onClick}
    >
      <CardContent className="py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Status Icon */}
            <span className="text-2xl" title={execution.status}>
              {getExecutionStatusIcon(execution.status)}
            </span>

            {/* Execution Info */}
            <div>
              <div className="flex items-center gap-2">
                {showAgentColumn && (
                  <Link
                    to={`/agents/${execution.agentId}`}
                    className="font-medium hover:text-primary hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {execution.agentName}
                  </Link>
                )}
                <span
                  className={`px-2 py-0.5 text-xs rounded-full font-medium ${getExecutionStatusColor(
                    execution.status
                  )}`}
                >
                  {execution.status}
                </span>
                <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-slate-100 text-slate-700">
                  {execution.priority}
                </span>
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                <span className="font-mono text-xs">{execution.id.slice(0, 8)}...</span>
                <span className="mx-2">|</span>
                Started: {formatDate(execution.startedAt || execution.createdAt)}
                {execution.duration !== null && (
                  <>
                    <span className="mx-2">|</span>
                    Duration: {formatDuration(execution.duration)}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* View Details Link */}
          <Link
            to={`/executions/${execution.id}`}
            className="text-sm text-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            View Details
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

export default ExecutionList;
